const DAY_MS = 24 * 60 * 60 * 1000;
const REMINDER_LEAD_DAYS = 2;
const REMINDER_LOCAL_HOUR = 10;

type DateParts = { year: number; month: number; day: number };

function readDateParts(ms: number, timeZone: string): DateParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
}

/** Offset (ms) such that `localWallClockMs = utcMs + offset` in `timeZone` at `utcMs`. */
function timezoneOffsetMs(utcMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") === 24 ? 0 : get("hour"),
    get("minute"),
    get("second"),
  );
  return asUtc - utcMs;
}

/** Converts a local wall-clock time in `timeZone` to a UTC instant (ms). */
function zonedTimeToUtcMs(
  { year, month, day }: DateParts,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  let guessMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  // Two passes converge even across a DST boundary at the target wall-clock time.
  for (let i = 0; i < 2; i += 1) {
    const offset = timezoneOffsetMs(guessMs, timeZone);
    guessMs = Date.UTC(year, month - 1, day, hour, minute, 0) - offset;
  }
  return guessMs;
}

/**
 * Computes the Day-5 trial reminder send time: {@link REMINDER_LEAD_DAYS} days
 * before `trialEndsAtMs`, at {@link REMINDER_LOCAL_HOUR}:00 local time in
 * `timeZone`. Falls back to a fixed UTC offset if `timeZone` is not a
 * recognized IANA identifier.
 */
export function computeTrialReminderNotifyAt(trialEndsAtMs: number, timeZone: string): number {
  const targetDayMs = trialEndsAtMs - REMINDER_LEAD_DAYS * DAY_MS;
  try {
    const parts = readDateParts(targetDayMs, timeZone);
    return zonedTimeToUtcMs(parts, REMINDER_LOCAL_HOUR, 0, timeZone);
  } catch {
    const date = new Date(targetDayMs);
    date.setUTCHours(REMINDER_LOCAL_HOUR, 0, 0, 0);
    return date.getTime();
  }
}
